/**
 * Zustand auth store — T20.
 *
 * Holds access-token state and user identity. Persists nothing to localStorage
 * (refresh cookie does cross-reload state; token lives in memory only).
 *
 * Wires itself to the api client singleton via wireAuthStore() so the client
 * can read the current token without creating a circular import.
 */
import { create } from 'zustand';
import { wireAuthStore } from '../api/client';
import { authApi } from '../api/endpoints/auth';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'writer' | 'admin' | 'system_owner';
  status: 'active' | 'inactive';
}

interface AuthState {
  accessToken: string | null;
  accessTokenExpiresAt: Date | null;
  user: User | null;
  /** Non-null when the current session is an impersonation of another user. */
  impAdminId: string | null;
  /** True when the current token carries an imp_admin_id JWT claim. */
  isImpersonating: boolean;
  /**
   * Holds the admin's own access token while they are impersonating someone else.
   * Set during the impersonation start flow (T27). Restored by stopImpersonating().
   */
  prevAccessToken: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Called by ApiClient on consecutive 401s.
   * Clears all auth state and navigates to /login.
   * Uses window.location.href rather than React Router because the store has
   * no router reference — the AuthGuard (T21) also listens for null token.
   */
  forceLogout: () => void;
  fetchMe: () => Promise<void>;
  /**
   * Called by ApiClient after a successful silent token refresh so React state
   * is updated even when a refresh happens mid-request.
   */
  setAccessToken: (token: string, expiresAt: Date) => void;
  /**
   * Swaps the current admin session for an impersonation session.
   *
   * Saves the admin's current accessToken as prevAccessToken (so
   * stopImpersonating can restore it), installs the new writer-scoped token,
   * decodes the JWT for imp_admin_id, and re-fetches /auth/me to load the
   * impersonated user. After this resolves, isImpersonating is true and the
   * banner renders on every page.
   */
  startImpersonating: (token: string, expiresAt: Date) => Promise<void>;
  /**
   * Restores the admin's own session after stopping impersonation.
   * Clears impAdminId / isImpersonating and restores prevAccessToken. If a
   * prev token was saved, re-fetches /auth/me to repopulate the admin user.
   * Called by ImpersonationBanner (T21) after stopImpersonation API call succeeds.
   */
  stopImpersonating: () => Promise<void>;
}

// ─── JWT decode helper ────────────────────────────────────────────────────────

/**
 * Lightweight base64 decode of the JWT payload section.
 * Does NOT verify the signature — trust is established server-side.
 * Used only to read the `imp_admin_id` claim to set UI state.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    // Base64url → base64 → decode
    const base64 = (parts[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  impAdminId: null,
  isImpersonating: false,
  prevAccessToken: null,

  async login(email, password) {
    const response = await authApi.login(email, password);
    const { accessToken, accessTokenExpiresAt, user } = response as {
      ok: true;
      accessToken: string;
      accessTokenExpiresAt: string;
      user: User;
    };

    const claims = decodeJwtPayload(accessToken);
    const impAdminId = typeof claims.imp_admin_id === 'string' ? claims.imp_admin_id : null;

    set({
      accessToken,
      accessTokenExpiresAt: new Date(accessTokenExpiresAt),
      user: {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        status: user.status,
      },
      impAdminId,
      isImpersonating: impAdminId !== null,
    });

    // Sync the singleton client so future requests carry the new token
    wireAuthStore(get());
  },

  async logout() {
    try {
      await authApi.logout();
    } catch {
      // Always clear local state even if the server call fails
    }
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      user: null,
      impAdminId: null,
      isImpersonating: false,
    });
    wireAuthStore(get());
  },

  async refresh() {
    const response = await authApi.refresh();
    const { accessToken, accessTokenExpiresAt } = response as {
      ok: true;
      accessToken: string;
      accessTokenExpiresAt: string;
    };
    set({
      accessToken,
      accessTokenExpiresAt: new Date(accessTokenExpiresAt),
    });
    wireAuthStore(get());
  },

  forceLogout() {
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      user: null,
      impAdminId: null,
      isImpersonating: false,
    });
    // Navigate away from any protected route
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  },

  async fetchMe() {
    const response = await authApi.me();
    const { user } = response as { ok: true; user: User };
    set({ user });
  },

  setAccessToken(token, expiresAt) {
    set({ accessToken: token, accessTokenExpiresAt: expiresAt });
    wireAuthStore(get());
  },

  async startImpersonating(token, expiresAt) {
    const prevAccessToken = get().accessToken;
    const claims = decodeJwtPayload(token);
    const impAdminId = typeof claims.imp_admin_id === 'string' ? claims.imp_admin_id : null;

    // Install the new token first so the /auth/me call below carries it.
    set({
      prevAccessToken,
      accessToken: token,
      accessTokenExpiresAt: expiresAt,
      impAdminId,
      isImpersonating: impAdminId !== null,
      // Clear the admin's user object until /auth/me returns the impersonated user.
      user: null,
    });
    wireAuthStore(get());

    // Fetch the impersonated user — without this, the UI keeps the previous
    // admin's name + role, which is what caused "Insufficient permissions"
    // pages to render against the admin's stale role while API calls used
    // the writer-scoped token.
    try {
      const response = await authApi.me();
      const { user } = response as { ok: true; user: User };
      set({ user });
    } catch {
      // If /auth/me fails for any reason, fall back to logging out so the
      // user isn't left in a half-authenticated state.
      set({
        accessToken: null,
        accessTokenExpiresAt: null,
        user: null,
        impAdminId: null,
        isImpersonating: false,
        prevAccessToken: null,
      });
      wireAuthStore(get());
    }
  },

  async stopImpersonating() {
    const prev = get().prevAccessToken;
    set({
      impAdminId: null,
      isImpersonating: false,
      prevAccessToken: null,
      // If the admin's prev token was saved, restore it; otherwise clear to
      // force a fresh login (fallback for sessions started before T27 saved it).
      accessToken: prev ?? null,
      user: null,
    });
    wireAuthStore(get());

    if (!prev) {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return;
    }

    // Re-fetch the admin's own user record so the UI repopulates with their
    // identity + role (without this, every page renders against user=null
    // and most protected routes fall back to read-only or empty states).
    try {
      const response = await authApi.me();
      const { user } = response as { ok: true; user: User };
      set({ user });
    } catch {
      // If /auth/me fails after restoring, force a fresh login.
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  },
}));

// Wire the client singleton on module load so requests during the initial
// render (before any login) correctly read the null token.
wireAuthStore(useAuthStore.getState());

// Keep the singleton wired whenever the store changes (token updates, etc.)
useAuthStore.subscribe((state) => {
  wireAuthStore(state);
});
